import {debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DataService} from '../../services/data.service';
import {AuthService} from '../../services/auth.service';
import {AppStateService} from '../../services/app-state.service';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, MatSortable, MatSortHeader} from '@angular/material/sort';
import {DataSource} from '@angular/cdk/collections';
import {BehaviorSubject, merge, Observable, Subject} from 'rxjs';
import {ResumeMaterial, SearchModes} from '../../interfaces';
import {Company} from '../../interfaces/address';

import {ActivatedRoute, ParamMap, Router, RouterLink} from '@angular/router';
import {AclUser} from '../../interfaces/app-user';
import {Order, OrderEntry} from '../../interfaces/order';
import {Timestamp} from 'firebase/firestore';
import {OrderService} from '../../services/orders.service';
import {MatIcon} from '@angular/material/icon';
import {MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {TimestampPipe} from '../../shared/pipes/timestamp.pipe';
import {CurrencyPipe, DatePipe} from '@angular/common';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatFormField, MatInput} from '@angular/material/input';
import {MatAnchor, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatSlideToggle} from '@angular/material/slide-toggle';



@Component({
    selector: 'app-order-view',
    templateUrl: './order-view.component.html',
    imports: [
        MatIcon,
        MatCardContent,
        TimestampPipe,
        DatePipe,
        MatPaginator,
        MatCard,
        MatCardHeader,
        MatCardActions,
        MatCardTitle,
        MatCell,
        MatHeaderCell,
        MatRow,
        MatHeaderRow,
        MatTable,
        MatProgressSpinner,
        MatFormField,
        CurrencyPipe,
        RouterLink,
        MatInput,
        MatSort,
        MatSortHeader,
        MatColumnDef,
        MatAnchor,
        MatHeaderRowDef,
        MatCellDef,
        MatRowDef,
        MatIconButton,
        MatTooltip,
        MatHeaderCellDef,
        MatHeaderCellDef,
        MatSlideToggle
    ],
    styleUrls: ['./order-view.component.scss']
})
export class OrderViewComponent implements OnInit, OnDestroy, AfterViewChecked {

    // For the mat-table
    readonly compactColumns: string[] = ['project', 'description', 'quantity', 'pricePerUnit', 'total', 'invoices'];
    readonly fullColumns: string[] = ['project', 'subBudget', 'description', 'unit', 'quantity', 'pricePerUnit', 'discount', 'total', 'invoices', 'invoiceCompletion', 'ddt', 'ddtCompletion'];
    public isCompactColumns = true;
    private canSeeOutOfBudget = false;
    displayedColumns: string[] = [...this.fullColumns];
    dataSource: ItemDataSource;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;
    public orderForm: UntypedFormGroup;
    public orderNr: string;
    public currentOrderNr: string;
    public currentOrderDate: any;
    public selectedVendor: Partial<Company>;
    public people;
    public oId;
    public order: Order;
    private _canAuthOrder;
    get canAuthOrder() {
        return this._canAuthOrder;
    }

    disableAuthorization = false;
    private unsubscribe$ = new Subject();
    private itemsStream$ = new BehaviorSubject([]);

    private _canEditOrder;
    get canEditOrder() {
        return this._canEditOrder;
    }

    constructor(
        private _changeDetectionRef: ChangeDetectorRef,
        private dataService: DataService,
        private authService: AuthService,
        private fb: UntypedFormBuilder,
        private router: Router,
        private route: ActivatedRoute,
        private appState: AppStateService,
        private orderService: OrderService) {
    }

    ngAfterViewChecked(): void {
        this._changeDetectionRef.detectChanges();
    }

    ngOnInit() {
        console.log('Begin.');

        try {
            const saved = sessionStorage.getItem('orderViewCompactColumns');
            if (saved !== null) {
                this.isCompactColumns = JSON.parse(saved);
            }
        } catch {
            // ignore
        }
        this.updateDisplayedColumns();

        this.route.paramMap.pipe(
            filter((params: ParamMap) => params.has('id')),
            switchMap((params: ParamMap) => {
                this.oId = params.get('id');
                console.log('Init order-view! orderId: ', this.oId);
                return this.orderService.getOrder(this.oId);
            })
        ).subscribe((order) => {
            if (!order) {
                this.appState.showError({errorCode: -1, errorMessage: 'Unable to find this order id'});
                this.router.navigate(['/order']);
            } else {
                order.id = this.oId;
                this.order = order;
                this.currentOrderNr = order.orderNr;
                this.selectedVendor = order.vendor;
                this.currentOrderDate = order.date;
                if (order.deliveryDate && !(order.deliveryDate instanceof Date)) {
                    order.deliveryDate = (order.deliveryDate as Timestamp).toDate();
                }

                this.setItemProperties(order.items);


                this.itemsStream$.next(order.items);
                console.log('Order retrieved and set: ', order);
            }
        });

        this.authService.userHasPermissions(['app.order.auth', 'app.order.edit'])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((val) => {
                this._canAuthOrder = val[0];
                this._canEditOrder = val[1];
                this.canSeeOutOfBudget = !!(val && (val[0] || val[1]));
                this.updateDisplayedColumns();
            });

        this.selectedVendor = undefined;

        this.buildForm();

        this.orderService.getNextOrderNr()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((num) => {
                const paddedNum = num.toString().padStart(4, '0');
                const year = (new Date).getFullYear();
                this.orderNr = this.authService.currentUser.initials + 'OF ' + paddedNum + '-' + year;
            });

        this.people = this.orderForm.get('vendor').valueChanges.pipe(
            distinctUntilChanged(),
            filter(vendor => (vendor && vendor.id)),
            switchMap((vendor) => {
                return this.dataService.getPeopleByCompany(vendor.id);
            }));

        // Assign the data to the data source for the table to render
        this.dataSource = new ItemDataSource(
            this.itemsStream$,
            this.paginator, this.sort);

    }

    public orderAuth(order: Order, isAuth: boolean) {
        this.disableAuthorization = true;
        this.orderService.ackOrderAuth(order, isAuth)
            .subscribe((res) => {
                    console.log('Authorization confirmed', res);
                },
                (err) => {
                    console.log('Error while authorizing order', err);
                });
    }

    ngOnDestroy() {
        this.unsubscribe$.next(undefined);
        this.unsubscribe$.complete();
    }

    public getListFilter() {
        const listFilter = localStorage.getItem('orderViewListFilter') ? localStorage.getItem('orderViewListFilter') : '';
        this.applyFilter(listFilter);
        return listFilter;
    }

    public setListFilter(filterName, filterValue) {
        localStorage.setItem(filterName, filterValue);
    }

    applyFilter(event: Event | string) {
        let filterValue: string;
        if (typeof event === 'string') {
            filterValue = event;
        } else {
            filterValue = (event.target as HTMLInputElement).value;
        }
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
        this.dataSource.filter = filterValue;
        this.setListFilter('orderViewListFilter', filterValue);
    }

    private buildForm() {
        // Definition of the Order Form
        // build the form model
        this.orderForm = this.fb.group({
            vendor: [undefined, Validators.required],
            primaryContact: [undefined, Validators.compose([Validators.required, this.primaryContactValidator])],
            techContact: null as AclUser,
            note: '',
            pmNote: undefined as string,
            paymentMethod: undefined as unknown as string,
            shippingMethod: undefined as string,
            returnMethod: undefined as string,
            deliveryDate: undefined as Date,
            vat: 22,
            invoice: this.fb.group({
                invoiceNr: undefined as string,
                date: undefined as Date
            }),
            deliveryNote: this.fb.group({
                deliveryNoteNr: undefined as string,
                date: undefined as Date,
                reason: undefined as string
            }),
            items: this.fb.array([]),
            // https://alligator.io/angular/reactive-forms-formarray-dynamic-fields/
            invoices: this.fb.array([]),
            deliveryNotes: this.fb.array([])
        });
    }

    private primaryContactValidator = (control): Record<string, any> => {
        if (!control.value) {
            return {'invalidPrimaryContact': {value: control.value}};
        }
        return (typeof control.value === 'object'
            && control.value.emails && control.value.emails.length > 0) ? null : {'invalidPrimaryContact': {value: control.value}};
    }

    private async setItemProperties(items: OrderEntry[]) {
        await Promise.all(items.map(async (item) => {
            // TODO use from a library
            item.total = (item.costEach * item.quantity) - (item.costEach * item.quantity / 100 * item.discount);
            let ddtCompletion = 0;
            if (item.ddt) {
                item.ddt.forEach(ddt => {
                    ddtCompletion = ddtCompletion + ddt.deliveryQuantity;
                });
            }

            item.itemDeliveryCompletion = (ddtCompletion / item.quantity * 100);

            const invoiceCompletion = item.invoices ? item.invoices.map(i => i.materialAmount).reduce((a, b) => a + b, 0) : 0;

            item.itemInvoiceCompletion = item.total - invoiceCompletion;

            // in Budget or out of Budget?
            if (item.budget.id) {
                await this.setOutOfBudget(item);
            } else {
                console.log('setOutOfBudget - cannot set out of budget for this item, no budget ID found', item);
            }

        }));
    }

    private async setOutOfBudget(item) {
        return this.dataService.getSubBudgetResume(item.budget.id)
            .pipe(
                take(1))
            .subscribe((summary: any) => {
                if (!summary) {
                    console.warn('setOutOfBudget - cannot calculate out of budget for budget: ', item.budget.id);
                    item.outOfBudget = null;
                }
                const material: ResumeMaterial = summary.materials
                    .find(m =>
                        m.id === item.material.id ||
                        (m.description === item.material.description &&
                            m.category === item.material.category &&
                            m.costEach === item.material.costEach));

                if (!material) {
                    console.log('setOutOfBudget - This item material is not in summary:', item);
                    item.outOfBudget = null;
                    return;
                }
                item.outOfBudget = material.referencedMaterialId ? false : (material.finalAmount > material.amount);
                console.log('setOutOfBudget - material for out of budget:', item.material, item.outOfBudget, summary);
            });
    }


    putInQuickList() {
        this.orderService.updateOrder(this.order.id, {searchMode: SearchModes.QUICK});
    }

    public toggleCompactColumns(compact: boolean) {
        this.isCompactColumns = compact;
        try {
            sessionStorage.setItem('orderViewCompactColumns', JSON.stringify(compact));
        } catch {
            // ignore
        }
        this.updateDisplayedColumns();
    }

    private updateDisplayedColumns() {
        if (this.isCompactColumns) {
            this.displayedColumns = [...this.compactColumns];
        } else {
            const cols = [...this.fullColumns];
            if (this.canSeeOutOfBudget && !cols.includes('outOfBudget')) {
                cols.push('outOfBudget');
            }
            this.displayedColumns = cols;
        }
    }

}


class ItemDataSource extends DataSource<Order> {

    _filterChange = new BehaviorSubject('');
    _dataChange = new BehaviorSubject([]);
    // The number of issues returned by github matching the query.
    public resultsLength = 0;
    public isLoadingResults = false;

    constructor(private observable,
                private _paginator: MatPaginator,
                private _sort: MatSort) {
        super();
        this.observable
            .subscribe(this._dataChange);
    }

    get filter(): string {
        return this._filterChange.value;
    }

    set filter(filterIt: string) {
        this._filterChange.next(filterIt);
    }

    /** Connect function called by the table to retrieve one stream containing the data to render. */
    connect(): Observable<Order[]> {
        // Listen for any changes in the base data, sorting, filtering, or pagination
        const displayDataChanges = [
            this.observable,
            this._sort.sortChange,
            this._filterChange.pipe(debounceTime(500)),
            this._paginator.page
        ];

        this._paginator._changePageSize(Number(localStorage.getItem('orderViewListPageSize')));
        this._paginator.page.next(new PageEvent());
        this._paginator.page.subscribe(paginationData => {
            localStorage.setItem('orderViewListPageSize', paginationData.pageSize.toString());
        });

        const sortId = localStorage.getItem('orderViewListSortId');
        const sortDirection = localStorage.getItem('orderViewListSortDirection');

        this._sort.sort(({id: sortId, start: sortDirection}) as MatSortable);

        return merge(...displayDataChanges).pipe(map(() => {
            const data = this._dataChange.value;

            // Filter
            const fData = data.filter((item: Order) => {
                let srcStr;
                try {
                    srcStr = '';
                } catch (err) {
                    srcStr = '';
                }

                return srcStr.indexOf(this.filter.toLowerCase()) !== -1;
            });

            this.resultsLength = fData.length;
            const startIndex = this._paginator.pageIndex * this._paginator.pageSize;
            this.sortData(fData);
            return fData.splice(startIndex, this._paginator.pageSize);
        }));

    }

    disconnect() {
    }

    private sortData = (timereport: Order[]) => {
        localStorage.setItem('orderViewListSortId', this._sort.active);
        localStorage.setItem('orderViewListSortDirection', this._sort.direction);
        if (this._sort.active === undefined || this._sort.direction === undefined) {
            return;
        }
        timereport.sort((a: Order, b: Order) => {

            let propertyA: number | string | Date = '';
            let propertyB: number | string | Date = '';

            switch (this._sort.active) {
                case 'vendor':
                    [propertyA, propertyB] = [a.vendor.name, b.vendor.name];
                    break;
                case 'date':
                    [propertyA, propertyB] = [(a.date as Date), (b.date as Date)];
                    break;
                case 'author':
                    [propertyA, propertyB] = [a.author.email, b.author.email];
                    break;
            }

            let valueA;
            let valueB;

            if (propertyA instanceof Date) {
                valueA = propertyA;
                valueB = propertyB;
            } else {
                valueA = isNaN(+propertyA) ? propertyA : +propertyA;
                valueB = isNaN(+propertyB) ? propertyB : +propertyB;
            }

            return (valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1);
        });
    }
}
